import { Plus, Trash2 } from 'lucide-react';
import { IconButton } from '../../components/IconButton';
import { Modal } from '../../components/Modal';
import type { RepositorySettings } from '../../domain/types';
import { createContactLink } from '../../domain/utils';
import type { TranslationKey } from '../../i18n';
import ui from '../../components/ui.module.css';
import styles from './ImportExport.module.css';

interface ExportDialogProps {
  open: boolean;
  settings: RepositorySettings;
  exporting: boolean;
  t: (key: TranslationKey, values?: Record<string, string | number>) => string;
  onClose: () => void;
  onChange: (settings: RepositorySettings) => void;
  onExport: () => void;
}

export function ExportDialog({
  open,
  settings,
  exporting,
  t,
  onClose,
  onChange,
  onExport,
}: ExportDialogProps) {
  return (
    <Modal
      open={open}
      title={t('repositorySettings')}
      closeLabel={t('close')}
      onClose={onClose}
      footer={
        <>
          <button type="button" className={ui.button} onClick={onClose}>
            {t('cancel')}
          </button>
          <button
            type="button"
            className={ui.primaryButton}
            data-testid="export-zip"
            disabled={exporting}
            onClick={onExport}
          >
            {t('exportZip')}
          </button>
        </>
      }
    >
      <div className={styles.settingsGrid}>
        <label className={styles.checkField}>
          <input
            type="checkbox"
            checked={settings.blankIssuesEnabled}
            onChange={(event) =>
              onChange({ ...settings, blankIssuesEnabled: event.target.checked })
            }
          />
          {t('blankIssues')}
        </label>
        <div className={styles.contactHeading}>
          <strong>{t('contactLinks')}</strong>
          <button
            type="button"
            className={ui.button}
            onClick={() =>
              onChange({
                ...settings,
                contactLinks: [...settings.contactLinks, createContactLink()],
              })
            }
          >
            <Plus size={14} strokeWidth={1.8} aria-hidden="true" />
            {t('addContact')}
          </button>
        </div>
        {settings.contactLinks.map((contact, index) => (
          <section className={styles.contactCard} key={contact.uid}>
            <div className={styles.contactTitle}>
              <strong>{contact.name || t('contactName')}</strong>
              <IconButton
                icon={Trash2}
                label={t('removeContact', { index: index + 1 })}
                onClick={() =>
                  onChange({
                    ...settings,
                    contactLinks: settings.contactLinks.filter((item) => item.uid !== contact.uid),
                  })
                }
              />
            </div>
            <label className={styles.formField}>
              <span>{t('contactName')}</span>
              <input
                value={contact.name}
                onChange={(event) =>
                  onChange({
                    ...settings,
                    contactLinks: settings.contactLinks.map((item) =>
                      item.uid === contact.uid ? { ...item, name: event.target.value } : item,
                    ),
                  })
                }
              />
            </label>
            <label className={styles.formField}>
              <span>{t('contactUrl')}</span>
              <input
                type="url"
                value={contact.url}
                placeholder="https://example.com/support"
                onChange={(event) =>
                  onChange({
                    ...settings,
                    contactLinks: settings.contactLinks.map((item) =>
                      item.uid === contact.uid ? { ...item, url: event.target.value } : item,
                    ),
                  })
                }
              />
            </label>
            <label className={styles.formField}>
              <span>{t('contactAbout')}</span>
              <input
                value={contact.about}
                onChange={(event) =>
                  onChange({
                    ...settings,
                    contactLinks: settings.contactLinks.map((item) =>
                      item.uid === contact.uid ? { ...item, about: event.target.value } : item,
                    ),
                  })
                }
              />
            </label>
          </section>
        ))}
      </div>
    </Modal>
  );
}
